import React, { useCallback, useEffect, useState } from 'react';
import { CnTable, CnDialog, CnMessage, CnConfirmButton, CnBox, CnSwitch, CnTooltip } from '@sto/cn-ui';
import CForm from '@/AIChart/components/chat/components/chatComponents/CForm';
import styles from './index.module.scss';
import { getSubscriptionList, deleteSubscription, getSubscriptionDetail, switchStatus } from '@/services/fetchs';
import SubscriptionRecord from './record';
import { TIME_FREQUENCY_LABEL } from '@/const';
import { convertPushReceiver } from './utils';

/**
 * 获取订阅列表列配置
 * @param param0 
 * @returns 
 */
const getColumns = ({ refresh }) => [
  {
    title: '任务名称',
    dataIndex: 'name',
    lock: true,
  },
  // {
  //   title: '订阅类型', // FIX_MENU:固定菜单 SMART_SUBSCRIPTION:智能订阅
  //   dataIndex: 'type',
  // },
  {
    title: '推送对象',
    dataIndex: 'userName',
    cell: (v, i, r) => {
      const { pushReceiverMap } = r;
      if (pushReceiverMap?.type) {
        const { type, specifyMembers } = pushReceiverMap;
        const userNames = [];
        if (type.includes('ONESELF')) {
          userNames.push(v);
        }
        if (type.includes('SPECIFY_MEMBERS')) {
          specifyMembers.forEach(item => userNames.push(item.label || item.userName))
        }
        if (userNames.length === 1) {
          return userNames[0];
        } else if (userNames.length > 1) {
          return <CnTooltip
            style={{ zIndex: 1001 }}
            trigger={
              <div>
                <div>{userNames[0]}</div>
                <div>...</div>
              </div>
            }
            align="l"
          >
            {userNames.map(item => <div key={item}>{item}</div>)}
          </CnTooltip>          
        } else {
          return '--'
        }
      } else {
        return <div>{v}</div>
      }
    },
  },
  {
    title: '频率&时间',
    dataIndex: 'frequency',
    cell: (value) => {
      if (!value) {
        return '--'
      }
      return <div>{TIME_FREQUENCY_LABEL[value]}</div>
    },
  },
  {
    title: '推送时间',
    dataIndex: 'pushTime',
    cell: (v, i, r) => {
      const { pushTimeList } = r;
      if (!v && !pushTimeList.length) {
        return '--'
      }
      if (pushTimeList.length) {
        if (pushTimeList.length > 2) {
          return <CnTooltip
            style={{ zIndex: 1001 }}
            trigger={
              <div>
                {pushTimeList.slice(0, 2).map(item => <div key={item}>{item}</div>)}
                <div>...</div>
              </div>
            }
            align="l"
          >
            {pushTimeList.map(item => <div key={item}>{item}</div>)}
          </CnTooltip>
        }
        return pushTimeList.map(item => <div key={item}>{item}</div>);
      } else {
        return v;
      }
    },
  },
  {
    title: '推送状态',
    dataIndex: 'status',
    cell: (value, i, record) => {
      const onChange = (checked) => {
        switchStatus({
          subscriptionId: record.id,
          status: checked ? 1 : 0,
        }).then(res => {
          if (res.success) {
            refresh();
            CnMessage.success('订阅状态更新成功');
          } else {
            CnMessage.error(res.errMessage);
          }
        });
      }
      return <CnSwitch autoWidth defaultChecked={value === 1} onChange={onChange} />
    },
  },
  {
    title: '创建时间',
    dataIndex: 'createdAt',
  },
  {
    title: '更新时间',
    dataIndex: 'updatedAt',
  },
];

/**
 * 订阅设置
 * @returns 
 */
const SubscriptionSetting = () => {
  const [subscriptionList, setSubscriptionList] = useState([]);
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);

  // 获取订阅列表
  const  getData = useCallback((current, pageSize) => {    
    getSubscriptionList({
      currentPage: current,
      pageSize: pageSize,
    }).then(res => {
      if (res.success) {
        if (res.data.length) {
          setSubscriptionList(res.data);
          setTotal(res.total);
        } else {
          setSubscriptionList([]);
        }
      } else {
        setSubscriptionList([]);
        CnMessage.error(res.errMessage);
      }
    });
  }, [])

  useEffect(() => {
    getData(current, pageSize);
  }, [current, pageSize]);

  const toEdit = (record) => {
    getSubscriptionDetail(record.id).then(res => {
      if (res.success) {
        const { schema, config, subscriptionId } = res.data;
        const { schema: newSchema, config: newConfig } = convertPushReceiver(schema, config);
        const dialog = CnDialog.show({
          title: '编辑订阅任务',
          content: <CForm 
            content={newSchema} 
            initData={newConfig}
            taskType={null} 
            askQuestion={null} 
            isHistory={null} 
            subscriptionId={subscriptionId}
            onSuccessCallback={() => {
              dialog.hide();
              getData(current, pageSize);
            }}
            formContentStyle={{ maxHeight: '500px', overflow: 'auto' }} 
          />,
          width: 660,
          onOk: () => {
            getData(current, pageSize);
          },
          className: styles.subscriptionDialog,
          footer: false,
        });
      } else {
        CnMessage.error(res.errMessage);
      }
    });
  }
  
  const operateColumnObj = {
    width: 142,
    buttons: [
      {
        children: '编辑',
        type: 'primary',
        async onClick(event, record, index) {
          toEdit(record);
        },
      },
      (record, index) => {
        return (
          <CnConfirmButton
            type="primary"
            text
            btnContent="删除"
            dialogTitle="提示"
            dialogContent="确定要删除该订阅任务吗？"
            dialogOkProps={{
              children: '确认',
              onClick: () => {
                deleteSubscription(record.id).then(res => {
                  if (res.success) {
                    if (subscriptionList.length === 1 && current > 1) {
                      getData(current - 1, pageSize);
                    } else {
                      getData(current, pageSize);
                    }
                  }
                });
              }
            }}
            balloonProps={{
              align: 'br',
            }}
          />
        );
      },
      {
        children: '订阅记录',
        type: 'primary',
        async onClick(event, record, index) {
          CnDialog.show({
            title: '订阅推送记录',
            content: <div style={{ padding: 12 }}>
              <SubscriptionRecord id={record.id} />
            </div>,
            width: 660,
            className: styles.subscriptionDialog,
            footer: false,
          });
        },
      },
    ],
  };

  return (<div className={styles.subscriptionBox} id="subscription-box">
    <CnBox direction="column" style={{ height: '100%' }}>
      <div className={styles.subscriptionHeader}>
        订阅设置
      </div>

      <div className={styles.subscriptionContent}> 
        <CnTable
          storageKey="subscription-table"
          primaryKey="id"
          columns={getColumns({
            refresh: () => {
              getData(current, pageSize);
            }
          })}
          dataSource={subscriptionList}
          operateColumn={operateColumnObj}
          onPageChange={(pageProps) => {
            setCurrent(pageProps.currentPage);
            setPageSize(pageProps.pageSize);
          }}
          cellCopyEnable={false}
          pageProps={{
            total,
          }}
          hasStickyHeader
          hasStickyFooter
        />
      </div>
    </CnBox>
  </div>
  );
};

export default SubscriptionSetting;